import { buildExportFileName } from "@zentrack/config";
import {
  activeSessionElapsedSeconds,
  buildClientReportRows,
  buildCsvExportRows,
  buildDailyReportRows,
  buildHistoryDaySections,
  entrySpanSeconds,
  projectColorForIndex,
  renderCsv,
  type ActiveSession,
  type AppUsage,
  type Client,
  type InstalledApplicationOption,
  type Project,
  type ProjectSoftwareRule,
  type TimeEntry,
} from "@zentrack/domain";
import {
  DesktopApiError,
  type AppSettings,
  type CreateClientInput,
  type CreateManualEntryInput,
  type CreateProjectInput,
  type DeleteTimeEntryInput,
  type DesktopApi,
  type DesktopSnapshot,
  type ExportCsvInput,
  type ExportCsvResult,
  type ReportsSnapshot,
  type RemoveClientInput,
  type RemoveProjectInput,
  type StartTimerInput,
  type TrackingCapability,
  type TraySnapshot,
  type UpdateClientInput,
  type UpdateManualEntryInput,
  type UpdateProjectInput,
} from "@zentrack/desktop-api";

import { buildBackup, serializeBackup, type LocalBackup } from "./backup.js";
import {
  openDb,
  readPersistedState,
  writePersistedState,
  clearPersistedState,
  type OpenDbOptions,
} from "./indexed-db.js";
import { createError } from "./errors.js";
import { defaultState, hydrateState, type BrowserState } from "./state.js";

export * from "./backup.js";
export * from "./errors.js";
export * from "./indexed-db.js";
export * from "./state.js";
export * from "./storage-persistence.js";

export interface WebApiOptions extends OpenDbOptions {
  /** Override for the IndexedDB key (defaults to "main"). */
  stateKey?: string;
  /** Override the database name (defaults to "zentrack-web"). Mainly for tests. */
  dbName?: string;
}

// ---------------------------------------------------------------------------
// Sorting helpers
// ---------------------------------------------------------------------------

function sortClients(clients: Client[]): Client[] {
  return [...clients].sort((left, right) => {
    const nameCompare = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
    return nameCompare === 0 ? left.createdAt.localeCompare(right.createdAt) : nameCompare;
  });
}

function sortProjects(projects: Project[]): Project[] {
  return [...projects].sort((left, right) => {
    const nameCompare = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
    return nameCompare === 0 ? left.createdAt.localeCompare(right.createdAt) : nameCompare;
  });
}

function sortProjectSoftwareRules(rules: ProjectSoftwareRule[]): ProjectSoftwareRule[] {
  return [...rules].sort((left, right) => {
    const projectCompare = left.projectId.localeCompare(right.projectId);
    if (projectCompare !== 0) {
      return projectCompare;
    }

    const nameCompare = left.displayName.localeCompare(right.displayName, undefined, {
      sensitivity: "base",
    });
    return nameCompare === 0 ? left.createdAt.localeCompare(right.createdAt) : nameCompare;
  });
}

function sortTimeEntries(entries: TimeEntry[]): TimeEntry[] {
  return [...entries].sort((left, right) => right.startTime.localeCompare(left.startTime));
}

function sortAppUsage(appUsages: AppUsage[]): AppUsage[] {
  return [...appUsages].sort((left, right) => right.startTime.localeCompare(left.startTime));
}

function normalizeRequiredName(name: string, emptyMessage: string): string {
  const normalized = name.trim();

  if (!normalized) {
    throw createError("VALIDATION", emptyMessage);
  }

  return normalized;
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `tz-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Tracking / capability / snapshot helpers
// ---------------------------------------------------------------------------

function getBrowserTrackingCapability(): TrackingCapability {
  return {
    platform: "unknown",
    status: "unsupported",
    message: "Foreground-app tracking is only available in the native desktop app.",
    supportsInstalledApplications: false,
    supportsForegroundTracking: false,
    nativeIdleDetection: false,
  };
}

function getProjectLabel(state: BrowserState, projectId: string): string | null {
  const project = state.projects.find((candidate) => candidate.id === projectId);

  if (!project) {
    return null;
  }

  const client = state.clients.find((candidate) => candidate.id === project.clientId);
  return `${client?.name ?? "Unknown Client"} / ${project.name}`;
}

function getTraySnapshot(state: BrowserState): TraySnapshot {
  return {
    activeSession: state.activeSession,
    projectLabel: state.activeSession ? getProjectLabel(state, state.activeSession.projectId) : null,
    elapsedSeconds: state.activeSession
      ? activeSessionElapsedSeconds(
          state.activeSession.startTime,
          new Date(),
          state.activeSession.pausedDurationSeconds,
          state.activeSession.pausedAt,
        )
      : 0,
    canStopTimer: Boolean(state.activeSession),
  };
}

function getReportsSnapshot(state: BrowserState): ReportsSnapshot {
  const dailyRows = buildDailyReportRows(state.timeEntries, state.appUsages);
  const clientRows = buildClientReportRows(state.clients, state.projects, state.timeEntries);

  const clientByProject = new Map(
    state.projects.map((project) => [project.id, project.clientId]),
  );

  return {
    totalTrackedDurationSeconds: state.timeEntries.reduce(
      (total, entry) => total + entry.countedDurationSeconds,
      0,
    ),
    trackedClientCount: new Set(
      state.timeEntries
        .map((entry) => clientByProject.get(entry.projectId))
        .filter((value): value is string => Boolean(value)),
    ).size,
    trackedProjectCount: new Set(state.timeEntries.map((entry) => entry.projectId)).size,
    hasFilteredSessions: state.timeEntries.some(
      (entry) => entry.countedDurationSeconds < entrySpanSeconds(entry),
    ),
    dailyRows,
    clientRows,
  };
}

function makeSnapshot(state: BrowserState): DesktopSnapshot {
  return {
    appInfo: {
      name: "ZenTrack",
      version: "0.0.0",
    },
    clients: sortClients(state.clients),
    projects: sortProjects(state.projects),
    projectSoftwareRules: sortProjectSoftwareRules(state.projectSoftwareRules),
    timeEntries: sortTimeEntries(state.timeEntries),
    appUsages: sortAppUsage(state.appUsages),
    activeSession: state.activeSession,
    settings: state.settings,
    tray: getTraySnapshot(state),
    reports: getReportsSnapshot(state),
    trackingCapability: getBrowserTrackingCapability(),
  };
}

function assertProjectExists(state: BrowserState, projectId: string): Project {
  const project = state.projects.find((candidate) => candidate.id === projectId);

  if (!project) {
    throw createError("NOT_FOUND", "The selected project could not be found.");
  }

  return project;
}

function assertClientExists(state: BrowserState, clientId: string): Client {
  const client = state.clients.find((candidate) => candidate.id === clientId);

  if (!client) {
    throw createError("NOT_FOUND", "The selected client could not be found.");
  }

  return client;
}

function ensureManualRange(startTime: string, endTime: string): number {
  const span = entrySpanSeconds({ startTime, endTime });

  if (new Date(endTime) <= new Date(startTime) || span <= 0) {
    throw createError("INVALID_SESSION_RANGE", "The session end time must be later than the start time.");
  }

  return span;
}

function getTrackedUsageBounds(appUsages: AppUsage[], timeEntryId: string) {
  const usages = appUsages
    .filter((usage) => usage.timeEntryId === timeEntryId)
    .sort((left, right) => left.startTime.localeCompare(right.startTime));

  if (usages.length === 0) {
    return null;
  }

  return {
    startTime: usages[0]!.startTime,
    endTime: usages[usages.length - 1]!.endTime,
  };
}

function exportCsvInBrowser(
  input: ExportCsvInput | undefined,
  rows: ReturnType<typeof buildCsvExportRows>,
): ExportCsvResult {
  const fileName = input?.destinationPath?.split("/").pop() || buildExportFileName();
  const csv = renderCsv(rows);

  if (typeof document !== "undefined") {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.style.display = "none";
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return {
    fileName,
    filePath: input?.destinationPath || fileName,
    rowCount: rows.length,
  };
}

function normalizeSoftwareRules(
  projectId: string,
  rules: ProjectSoftwareRule[] | undefined,
  createdAt: string,
): ProjectSoftwareRule[] {
  const unique = new Map<string, ProjectSoftwareRule>();

  for (const rule of rules ?? []) {
    const displayName = rule.displayName.trim();
    const matchValue = rule.matchValue.trim();

    if (!displayName || !matchValue) {
      continue;
    }

    const normalized: ProjectSoftwareRule = {
      id: rule.id || makeId(),
      projectId,
      displayName,
      matcherType: rule.matcherType,
      matchValue,
      createdAt: rule.createdAt || createdAt,
    };

    unique.set(`${normalized.matcherType}:${normalized.matchValue}`, normalized);
  }

  return [...unique.values()];
}

// ---------------------------------------------------------------------------
// Public API factory
// ---------------------------------------------------------------------------

export interface WebDesktopApi extends DesktopApi {
  /** Export the current browser state as a serialized JSON backup. */
  exportBackup(): Promise<string>;
  /** Read the current state without producing a full snapshot. */
  readBackupState(): Promise<LocalBackup>;
  /**
   * Replace all local data with the given backup. The caller is responsible for
   * confirming the destructive overwrite (e.g. via an AlertDialog) before
   * invoking this.
   */
  importBackup(backup: LocalBackup): Promise<void>;
  /** Remove all local data and reset to an empty state. */
  clearLocalData(): Promise<void>;
}

export async function createWebDesktopApi(options: WebApiOptions = {}): Promise<WebDesktopApi> {
  const stateKey = options.stateKey ?? "main";
  const db = await openDb({ idb: options.idb, dbName: options.dbName });

  async function readState(): Promise<BrowserState> {
    const persisted = await readPersistedState(db, stateKey);
    return hydrateState(persisted);
  }

  async function writeState(state: BrowserState): Promise<void> {
    await writePersistedState(db, state, stateKey);
  }

  /**
   * Read the current state, apply a mutation, then persist a sorted result. The
   * updater is allowed to mutate the state in place; sorting runs before write.
   */
  async function withState<T>(updater: (state: BrowserState) => T): Promise<T> {
    const state = await readState();
    const result = updater(state);
    await writeState({
      ...state,
      clients: sortClients(state.clients),
      projects: sortProjects(state.projects),
      projectSoftwareRules: sortProjectSoftwareRules(state.projectSoftwareRules),
      timeEntries: sortTimeEntries(state.timeEntries),
      appUsages: sortAppUsage(state.appUsages),
    });
    return result;
  }

  const api: WebDesktopApi = {
    async bootstrap() {
      return makeSnapshot(await readState());
    },
    clients: {
      async list() {
        return sortClients((await readState()).clients);
      },
      async create(input: CreateClientInput) {
        return withState((state) => {
          const now = new Date().toISOString();
          const client: Client = {
            id: makeId(),
            name: normalizeRequiredName(input.name, "Client name is required."),
            createdAt: now,
          };

          state.clients.push(client);
          return client;
        });
      },
      async update(input: UpdateClientInput) {
        return withState((state) => {
          const client = assertClientExists(state, input.id);
          client.name = normalizeRequiredName(input.name, "Client name is required.");
          return client;
        });
      },
      async remove(input: RemoveClientInput) {
        return withState((state) => {
          const hasTrackedData = state.projects.some((project) => {
            if (project.clientId !== input.id) {
              return false;
            }

            return (
              state.timeEntries.some((entry) => entry.projectId === project.id) ||
              state.activeSession?.projectId === project.id
            );
          });

          if (hasTrackedData) {
            throw createError(
              "CLIENT_HAS_TRACKED_DATA",
              "This client cannot be deleted because one of its projects still has saved time entries or an active timer.",
            );
          }

          state.clients = state.clients.filter((client) => client.id !== input.id);
          state.projects = state.projects.filter((project) => project.clientId !== input.id);
        });
      },
    },
    projects: {
      async list() {
        return sortProjects((await readState()).projects);
      },
      async listSoftwareRules() {
        return sortProjectSoftwareRules((await readState()).projectSoftwareRules);
      },
      async create(input: CreateProjectInput) {
        return withState((state) => {
          assertClientExists(state, input.clientId);
          const now = new Date().toISOString();
          const project: Project = {
            id: makeId(),
            clientId: input.clientId,
            name: normalizeRequiredName(input.name, "Project name is required."),
            color: input.color?.trim() || projectColorForIndex(state.projects.length),
            createdAt: now,
          };

          state.projects.push(project);
          state.projectSoftwareRules = state.projectSoftwareRules
            .filter((rule) => rule.projectId !== project.id)
            .concat(normalizeSoftwareRules(project.id, input.softwareRules, now));
          return project;
        });
      },
      async update(input: UpdateProjectInput) {
        return withState((state) => {
          assertClientExists(state, input.clientId);
          const project = assertProjectExists(state, input.id);
          project.clientId = input.clientId;
          project.name = normalizeRequiredName(input.name, "Project name is required.");
          project.color = input.color?.trim() || project.color;
          state.projectSoftwareRules = state.projectSoftwareRules
            .filter((rule) => rule.projectId !== project.id)
            .concat(
              normalizeSoftwareRules(project.id, input.softwareRules, new Date().toISOString()),
            );
          return project;
        });
      },
      async remove(input: RemoveProjectInput) {
        return withState((state) => {
          const hasTrackedData =
            state.timeEntries.some((entry) => entry.projectId === input.id) ||
            state.activeSession?.projectId === input.id;

          if (hasTrackedData) {
            throw createError(
              "PROJECT_HAS_TRACKED_DATA",
              "This project cannot be deleted because it still has saved time entries or an active timer.",
            );
          }

          state.projects = state.projects.filter((project) => project.id !== input.id);
          state.projectSoftwareRules = state.projectSoftwareRules.filter(
            (rule) => rule.projectId !== input.id,
          );
        });
      },
    },
    timer: {
      async start(input: StartTimerInput) {
        return withState((state) => {
          if (state.activeSession) {
            throw createError(
              "ACTIVE_SESSION_ALREADY_EXISTS",
              "A timer is already running. Stop it before starting a new one.",
            );
          }

          assertProjectExists(state, input.projectId);
          const now = new Date().toISOString();
          const session: ActiveSession = {
            id: makeId(),
            projectId: input.projectId,
            note: input.note.trim(),
            startTime: now,
            createdAt: now,
            pausedAt: null,
            pausedDurationSeconds: 0,
          };

          state.activeSession = session;
          return session;
        });
      },
      async pause() {
        return withState((state) => {
          const activeSession = state.activeSession;
          if (!activeSession) {
            throw createError("NO_ACTIVE_SESSION", "No active timer is available to pause.");
          }

          if (activeSession.pausedAt) {
            throw createError("ACTIVE_SESSION_ALREADY_PAUSED", "The timer is already paused.");
          }

          activeSession.pausedAt = new Date().toISOString();
          return activeSession;
        });
      },
      async resume() {
        return withState((state) => {
          const activeSession = state.activeSession;
          if (!activeSession) {
            throw createError("NO_ACTIVE_SESSION", "No active timer is available to resume.");
          }

          if (!activeSession.pausedAt) {
            throw createError("ACTIVE_SESSION_NOT_PAUSED", "The timer is already running.");
          }

          const resumedAt = new Date();
          activeSession.pausedDurationSeconds += Math.max(
            0,
            Math.round(
              (resumedAt.getTime() - new Date(activeSession.pausedAt).getTime()) / 1000,
            ),
          );
          activeSession.pausedAt = null;
          return activeSession;
        });
      },
      async stop() {
        return withState((state) => {
          if (!state.activeSession) {
            throw createError("NO_ACTIVE_SESSION", "No active timer is available to stop.");
          }

          const activeSession = state.activeSession;
          const endedAt = activeSession.pausedAt
            ? new Date(activeSession.pausedAt)
            : new Date();
          const safeEndTime =
            endedAt <= new Date(activeSession.startTime)
              ? new Date(new Date(activeSession.startTime).getTime() + 1000)
              : endedAt;

          const entry: TimeEntry = {
            id: activeSession.id,
            projectId: activeSession.projectId,
            note: activeSession.note,
            startTime: activeSession.startTime,
            endTime: safeEndTime.toISOString(),
            countedDurationSeconds: Math.max(
              1,
              entrySpanSeconds({
                startTime: activeSession.startTime,
                endTime: safeEndTime.toISOString(),
              }) - activeSession.pausedDurationSeconds,
            ),
            createdAt: activeSession.createdAt,
          };

          state.timeEntries.push(entry);
          state.activeSession = null;
          return entry;
        });
      },
      async getCurrentSession() {
        return (await readState()).activeSession;
      },
    },
    history: {
      async listDays() {
        return buildHistoryDaySections((await readState()).timeEntries);
      },
      async createManualEntry(input: CreateManualEntryInput) {
        return withState((state) => {
          assertProjectExists(state, input.projectId);
          const span = ensureManualRange(input.startTime, input.endTime);
          const entry: TimeEntry = {
            id: makeId(),
            projectId: input.projectId,
            note: input.note.trim(),
            startTime: input.startTime,
            endTime: input.endTime,
            countedDurationSeconds: span,
            createdAt: new Date().toISOString(),
          };

          state.timeEntries.push(entry);
          return entry;
        });
      },
      async updateManualEntry(input: UpdateManualEntryInput) {
        return withState((state) => {
          assertProjectExists(state, input.projectId);
          const entry = state.timeEntries.find((candidate) => candidate.id === input.id);

          if (!entry) {
            throw createError("NOT_FOUND", "The selected time entry could not be found.");
          }

          const span = ensureManualRange(input.startTime, input.endTime);
          const usageBounds = getTrackedUsageBounds(state.appUsages, input.id);

          if (
            usageBounds &&
            (new Date(input.startTime) > new Date(usageBounds.startTime) ||
              new Date(input.endTime) < new Date(usageBounds.endTime))
          ) {
            throw createError(
              "TIME_ENTRY_EDIT_WOULD_EXCLUDE_TRACKED_USAGE",
              "The edited session range must still include the stored app-usage timeline.",
            );
          }

          entry.projectId = input.projectId;
          entry.note = input.note.trim();
          entry.startTime = input.startTime;
          entry.endTime = input.endTime;

          if (!usageBounds) {
            entry.countedDurationSeconds = span;
          }

          return entry;
        });
      },
      async deleteEntry(input: DeleteTimeEntryInput) {
        return withState((state) => {
          const index = state.timeEntries.findIndex((entry) => entry.id === input.id);

          if (index === -1) {
            throw createError("NOT_FOUND", "The selected time entry could not be found.");
          }

          state.timeEntries.splice(index, 1);
          state.appUsages = state.appUsages.filter((usage) => usage.timeEntryId !== input.id);
        });
      },
    },
    reports: {
      async getDashboard() {
        return getReportsSnapshot(await readState());
      },
      async exportCsv(input?: ExportCsvInput) {
        const state = await readState();
        const rows = buildCsvExportRows(
          state.timeEntries,
          state.appUsages,
          state.projects,
          state.clients,
        );
        return exportCsvInBrowser(input, rows);
      },
    },
    settings: {
      async get() {
        return (await readState()).settings;
      },
      async update(input: Partial<AppSettings>) {
        return withState((state) => {
          state.settings = {
            ...state.settings,
            ...input,
          };
          return state.settings;
        });
      },
    },
    window: {
      async showMain() {
        // No native window in the browser. Kept to satisfy the shared contract.
      },
    },
    tray: {
      async getState() {
        return getTraySnapshot(await readState());
      },
    },
    tracking: {
      async getCapability() {
        return getBrowserTrackingCapability();
      },
      async getInstalledApplications(): Promise<InstalledApplicationOption[]> {
        return [];
      },
      async refreshInstalledApplications(): Promise<InstalledApplicationOption[]> {
        return [];
      },
    },
    // Browser-only backup extensions:
    async exportBackup() {
      const state = await readState();
      return serializeBackup(buildBackup(state));
    },
    async readBackupState() {
      return buildBackup(await readState());
    },
    async importBackup(backup: LocalBackup) {
      await writeState(hydrateState(backup.data));
    },
    async clearLocalData() {
      await clearPersistedState(db, stateKey);
    },
  };

  return api;
}

export { defaultState, hydrateState, DesktopApiError };
export type { BrowserState };
